import { TrendingDown, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { t } from "@/i18n";
import { CountUp } from "@/components/dashboard/motion";

export const DSP_COLORS = { Spotify: "#19B26C", "Apple Music": "#FB5069", "Youtube Music": "#FF0033", "Amazon Music": "#25D0D8", Deezer: "#A238FF", Pandora: "#3668FF", Soundcloud: "#FF5500" };
export const SOCIAL_COLORS = { Tiktok: "#69C9D0", Facebook: "#1877F2", Instagram: "#E1306C", Meta: "#0081FB", Youtube: "#FF0000" };
export const SOCIAL_LABEL = { Tiktok: "TikTok", Youtube: "YouTube", Facebook: "Facebook", Instagram: "Instagram", Meta: "Meta (otros)" };
export const fmtNum = (n) => (n === null || n === undefined ? "—" : new Intl.NumberFormat().format(n));

export const PctBars = ({ items, color = "hsl(var(--primary))", testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    {items.map((x) => (
      <div key={x.label} className="grid grid-cols-[90px_1fr_52px] items-center gap-3 text-sm">
        <span className="truncate text-muted-foreground">{x.label}</span>
        <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bar-grow transition-[width] duration-700" style={{ width: `${Math.max(1, x.pct)}%`, backgroundColor: color }} /></div>
        <span className="text-right tabular-nums font-medium">{x.pct}%</span>
      </div>
    ))}
  </div>
);

export const Change = ({ pct, testId }) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-muted-foreground" data-testid={testId}>—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-400" : "text-rose-400"}`} data-testid={testId}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{pct.toFixed(2)}%
    </span>
  );
};

export const MetricCard = ({ label, metric, hint, testId }) => {
  const animate = typeof metric?.value === "number" && (!metric.display || metric.display === fmtNum(metric.value));
  return (
    <div className="panel p-4 group" data-testid={testId}>
      <p className="eyebrow">{t(label)}</p>
      <p className="font-display text-2xl font-bold mt-1 tabular-nums transition-colors group-hover:text-primary">{animate ? <CountUp value={metric.value} /> : (metric?.display ?? fmtNum(metric?.value))}</p>
      <div className="flex items-center justify-between mt-1">
        {(metric?.change_pct !== null && metric?.change_pct !== undefined) || !hint ? <Change pct={metric?.change_pct} /> : null}
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
    </div>
  );
};

export const DspBars = ({ items, field = "streams_7d", labelKey = "dsp", colors = DSP_COLORS, labels = {}, testId }) => {
  const total = items.reduce((a, x) => a + (x[field] || 0), 0) || 1;
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((x, i) => (
        <div key={x[labelKey]} className="grid grid-cols-[110px_1fr_70px] items-center gap-3 text-sm">
          <span className="truncate text-muted-foreground">{labels[x[labelKey]] || x[labelKey]}</span>
          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bar-grow transition-[width] duration-700" style={{ width: `${Math.max(2, ((x[field] || 0) / total) * 100)}%`, backgroundColor: colors[x[labelKey]] || "hsl(var(--primary))", animationDelay: `${0.1 + i * 0.06}s` }} /></div>
          <span className="text-right tabular-nums font-medium"><CountUp value={x[field] || 0} /></span>
        </div>
      ))}
    </div>
  );
};

export const DailyChart = ({ data, series, testId, height = "h-56" }) => (
  <div className={`${height} w-full chart-reveal`} data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`g-${s.key.replace(/\s/g, "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.35} /><stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} minTickGap={24} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "hsl(var(--muted-foreground))" }} />
        {series.map((s) => <Area key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stackId="1" stroke={s.color} strokeWidth={2} fill={`url(#g-${s.key.replace(/\s/g, "")})`} />)}
      </AreaChart>
    </ResponsiveContainer>
  </div>
);
